import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, User } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminLogin = () => {
  const { t } = useLanguage();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password,
      });

      localStorage.setItem("adminToken", response.data.access_token);
      if (response.data.role) localStorage.setItem("adminRole", response.data.role);
      if (response.data.name) localStorage.setItem("adminName", response.data.name);
      toast.success(t('admin_login_success'));
      navigate("/admin/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(t('admin_login_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a0f0f 0%, #2d1414 50%, #1a0f0f 100%)' }}>
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-900/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-900/20 rounded-full filter blur-3xl"></div>
      </div>
      
      {/* Language Switcher */}
      <div className="absolute top-6 right-6 z-20">
        <LanguageSwitcher />
      </div>
      
      <Card className="w-full max-w-md mx-4 bg-zinc-900 border-yellow-600/30 shadow-2xl shadow-yellow-900/20 relative z-10">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-yellow-700 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-white" />
            </div>
            <h1 className="luxury-title text-3xl font-bold text-white mb-2">{t('admin_panel')}</h1>
            <p className="luxury-subtitle text-gray-400">Rent Car Kaci</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="username" className="text-gray-300 luxury-text">{t('admin_username')}</Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                  id="username"
                  data-testid="admin-username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="pl-10 bg-zinc-800 border-gray-700 text-white focus:border-yellow-600"
                  placeholder={t('admin_enter_username')}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-300 luxury-text">{t('admin_password')}</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                  id="password"
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 bg-zinc-800 border-gray-700 text-white focus:border-yellow-600"
                  placeholder={t('admin_enter_password')}
                />
              </div>
            </div>

            <Button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-yellow-700 to-yellow-600 hover:from-yellow-600 hover:to-yellow-500 text-white py-6 luxury-text tracking-wider uppercase"
            >
              {loading ? t('admin_logging_in') : t('admin_login')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate("/")}
              className="text-gray-400 hover:text-yellow-600 transition-colors luxury-text text-sm"
            >
              {t('admin_back_home')}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
